import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogPortal,
  DialogOverlay,
} from '../../ui/Dialog';
import { LogOut, X } from 'lucide-react';
import { Button } from '../../ui/Button';
import { cn } from '../../../utils/cn';
import {
  MOBILE_NAVIGATION,
  ADMIN_MOBILE_NAVIGATION,
  B2B_MOBILE_NAVIGATION,
  B2B_ADMIN_MOBILE_NAVIGATION,
} from '../../../constants/Navigation';
import { useAuth } from '../../../hooks/useAuth';

interface MobileDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const MobileDrawer: React.FC<MobileDrawerProps> = ({
  open,
  onOpenChange,
}) => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const isAdmin = user?.roleName === 'ADMIN' || Number(user?.roleId) === 1 || Number(user?.roleId) === 3;
  const isB2B = location.pathname.startsWith('/b2b');

  const navigationItems = React.useMemo(() => {
    let items;
    if (isB2B) {
      items = isAdmin ? B2B_ADMIN_MOBILE_NAVIGATION : B2B_MOBILE_NAVIGATION;
    } else {
      items = MOBILE_NAVIGATION;
    }
    if (isAdmin) {
      return items.filter((item) => {
        if (
          item.id === 'information-center' ||
          item.id === 'support' ||
          item.id === 'integration' ||
          item.id === 'rate-calculator' ||
          item.id === 'rate-card'
        ) {
          return false;
        }
        return true;
      });
    }
    return items;
  }, [isB2B, isAdmin]);

  const adminItems = React.useMemo(() => {
    if (!isAdmin || isB2B) return [];
    return ADMIN_MOBILE_NAVIGATION;
  }, [isB2B, isAdmin]);

  const handleItemClick = () => {
    onOpenChange(false);
  };

  const handleLogout = async () => {
    try {
      await logout();
      onOpenChange(false);
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay className="fixed inset-0 bg-black/50 z-40" />
        <DialogContent className="w-full bg-sidebar p-0 fixed inset-0 h-[100dvh] overflow-hidden rounded-none m-0 z-50 flex flex-col">
          <div className="flex flex-col h-full relative">
            {/* Sidebar Texture */}
            <div className="absolute bottom-0 left-0 right-0 h-[60%] pointer-events-none z-0 opacity-100">
              <img
                src="/sidebarTexture.png"
                alt="Sidebar texture"
                className="w-full h-full object-cover object-left-bottom"
              />
            </div>

            {/* Header */}
            <div className="p-6 border-b border-[#D9D9D980] flex-shrink-0 flex items-center justify-between relative z-10">
              <img
                src={isB2B ? '/allwhite.svg' : '/sidebarLogo.svg'}
                alt="Logo"
                className="h-12 object-contain max-w-[200px]"
              />
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onOpenChange(false)}
                className="text-white hover:bg-white/10"
              >
                <X className="h-6 w-6" />
              </Button>
            </div>

            {/* Scrollable nav */}
            <nav className="flex-1 overflow-y-auto p-4 pb-20 relative z-10">
              <div className="flex flex-col">
                <div className="grid grid-cols-3 gap-4">
                  {navigationItems.map((item) => {
                    const isActive = location.pathname === item.path;
                    const Icon = item.icon;
                    const isInactive = (item as any).inactive;

                    return (
                      <Link
                        key={item.id}
                        to={isInactive ? '#' : item.path}
                        className={cn(
                          'flex flex-col items-center justify-center p-4 rounded-xl transition-all duration-200 relative',
                          isInactive
                            ? 'opacity-50 grayscale cursor-not-allowed'
                            : 'hover:bg-accent hover:scale-105 active:scale-95',
                          isActive && !isInactive
                            ? 'bg-foreground text-text2 shadow-white shadow'
                            : 'bg-none text-foreground shadow-sm hover:shadow-md'
                        )}
                        onClick={
                          isInactive
                            ? (e) => e.preventDefault()
                            : handleItemClick
                        }
                      >
                        <div className="mb-2">
                          <Icon className="h-8 w-8" />
                        </div>
                        <span className="text-sm font-medium text-center leading-tight">
                          {item.label}
                        </span>
                        {isInactive && (
                          <span className="absolute top-1 right-1 bg-gray-500 text-[8px] text-white px-1 rounded">
                            Soon
                          </span>
                        )}
                      </Link>
                    );
                  })}
                </div>

                {adminItems.length > 0 && (
                  <div className="mt-6 border-t border-[#D9D9D940] pt-4">
                    <h4 className="text-white/80 text-xs font-semibold uppercase tracking-wider mb-3 px-1">
                      Admin Controls
                    </h4>
                    <div className="grid grid-cols-3 gap-4">
                      {adminItems.map((item) => {
                        const isActive = location.pathname === item.path;
                        const Icon = item.icon;

                        return (
                          <Link
                            key={item.id}
                            to={item.path}
                            className={cn(
                              'flex flex-col items-center justify-center p-4 rounded-xl transition-all duration-200 relative hover:bg-accent hover:scale-105 active:scale-95',
                              isActive
                                ? 'bg-foreground text-text2 shadow-white shadow'
                                : 'bg-none text-foreground shadow-sm hover:shadow-md'
                            )}
                            onClick={handleItemClick}
                          >
                            <div className="mb-2">
                              <Icon className="h-8 w-8" />
                            </div>
                            <span className="text-sm font-medium text-center leading-tight">
                              {item.label}
                            </span>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Footer with Logout */}
                <div className="mt-8 border-t pt-4 flex-shrink-0">
                  <button
                    className="flex items-center justify-center gap-3 p-4 rounded-xl w-full transition-all duration-200 bg-background border border-border hover:bg-destructive hover:text-white group"
                    onClick={handleLogout}
                  >
                    <LogOut className="h-6 w-6 text-red-500 group-hover:text-white" />
                    <span className="font-bold text-red-500 text-xl group-hover:text-white">
                      Logout
                    </span>
                  </button>
                </div>
              </div>
            </nav>
          </div>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
};
